import React, { useState, useEffect, useCallback } from 'react'

// Inject shared keyframes once
if (typeof document !== 'undefined' && !document.getElementById('map-demand-arc-anim')) {
  const el = document.createElement('style')
  el.id = 'map-demand-arc-anim'
  el.textContent = `
    @keyframes mapDemandDash {
      from { stroke-dashoffset: 22; }
      to   { stroke-dashoffset: 0; }
    }
  `
  document.head.appendChild(el)
}

// Must mirror the same offset formula used in MapDemandMarker
function getPixelOffset(index, total) {
  if (total === 1) return [38, 38]
  const step = 46
  const startX = -((total - 1) * step) / 2
  return [startX + index * step, 44]
}

function MapDemandArcLayer({ mapRef, demands, nodes, demandsByTarget, demandPositions, visible }) {
  const [arcs, setArcs] = useState([])

  const computeArcs = useCallback(() => {
    if (!mapRef?.current) return
    const result = []

    demands.forEach(demand => {
      const targetNode = nodes.find(n => n.id === demand.target_node)
      if (!targetNode?.data?.lat || !targetNode?.data?.lng) return

      const toPx = mapRef.current.project([targetNode.data.lng, targetNode.data.lat])

      let fromPx
      const customPos = demandPositions?.[demand.id]
      if (customPos) {
        // Marker was dragged — use its actual lat/lng
        fromPx = mapRef.current.project([customPos.lng, customPos.lat])
      } else {
        // Default pixel offset from target node
        const siblings = demandsByTarget[demand.target_node] || []
        const index = siblings.indexOf(demand)
        const total = siblings.length
        const [offX, offY] = getPixelOffset(index, total)
        fromPx = { x: toPx.x + offX, y: toPx.y + offY }
      }

      result.push({ id: demand.id, from: fromPx, to: toPx })
    })

    setArcs(result)
  }, [mapRef, demands, nodes, demandsByTarget, demandPositions])

  useEffect(() => {
    const map = mapRef?.current?.getMap?.()
    if (!map) return
    computeArcs()
    map.on('move',   computeArcs)
    map.on('resize', computeArcs)
    return () => {
      map.off('move',   computeArcs)
      map.off('resize', computeArcs)
    }
  }, [mapRef, computeArcs])

  if (!visible || arcs.length === 0) return null

  return (
    <svg
      style={{
        position: 'absolute', top: 0, left: 0,
        width: '100%', height: '100%',
        pointerEvents: 'none', overflow: 'visible', zIndex: 3,
      }}
    >
      {arcs.map(({ id, from, to }) => {
        // Quadratic bezier — control point pushed perpendicular to the midpoint
        const dx  = to.x - from.x
        const dy  = to.y - from.y
        const mx  = (from.x + to.x) / 2
        const my  = (from.y + to.y) / 2
        const cpX = mx - dy * 0.4
        const cpY = my + dx * 0.4
        const d   = `M ${from.x} ${from.y} Q ${cpX} ${cpY} ${to.x} ${to.y}`

        return (
          <g key={id}>
            {/* Wide glow halo */}
            <path
              d={d}
              stroke="#f43f5e"
              strokeWidth={10}
              fill="none"
              opacity={0.06}
              strokeLinecap="round"
            />
            {/* Medium glow */}
            <path
              d={d}
              stroke="#f43f5e"
              strokeWidth={4}
              fill="none"
              opacity={0.12}
              strokeLinecap="round"
            />
            {/* Animated flowing dashes */}
            <path
              d={d}
              stroke="#f43f5e"
              strokeWidth={1.5}
              fill="none"
              strokeLinecap="round"
              strokeDasharray="6 4"
              opacity={0.7}
              style={{ animation: 'mapDemandDash 0.75s linear infinite' }}
            />
          </g>
        )
      })}
    </svg>
  )
}

export default MapDemandArcLayer
